import { TFlatJsonStruct } from '@app/factory/flatJson';
import { foundSameValue } from '@app/factory/foundSameValue';
import { Alert } from '@app/modules/Translate/components/Alert/Alert';
import { Modal } from '@app/modules/Translate/components/Table/Modal';
import { TranslateCategory } from '@app/modules/Translate/components/TranslateCategory/TranslateCategory';
import { TextField } from '@mui/material';
import { Box } from '@mui/system';
import { CellClickedEvent, CellValueChangedEvent, GridOptions, GridReadyEvent, RowNode } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { observe } from 'mobx';
import React, { FunctionComponent, useEffect } from 'react';
import { useStorageTranslateContext } from '../../contexts/storage';
import { columnDefs } from '../../factory/columnDefs';
import './Table.scss';

export const Table: FunctionComponent = () => {
    const { translateStore } = useStorageTranslateContext();

    const gridOptions: GridOptions<TFlatJsonStruct> = {
        columnDefs: columnDefs(),
        getRowId(data) {
            return data.data.key;
        },
        onGridReady(event: GridReadyEvent): void {
            translateStore.gridApi = event.api;
            translateStore.gridApi.sizeColumnsToFit();
        },
        rowSelection: 'single',
        isExternalFilterPresent() {
            return true;
        },
        doesExternalFilterPass(node: RowNode): boolean {
            if (translateStore.hideWords) {
                return (node.data as TFlatJsonStruct).translate === '';
            } else if (translateStore.changeWords) {
                return translateStore.changeSet.has(node.data.key) || translateStore.changeSetRest.has(node.data.key);
            }
            return true;
        },
        onCellEditingStopped(event) {
            setTimeout(() => {
                translateStore.gridApi &&
                    !translateStore.gridApi.isDestroyed() &&
                    translateStore.gridApi.redrawRows({
                        rowNodes: [event.node]
                    });
            });
        },
        onCellClicked(event: CellClickedEvent): void {
            translateStore.eventNewValue = '';
            translateStore.eventOldValue = '';
        },
        onCellValueChanged(event: CellValueChangedEvent) {
            translateStore.eventNewValue = event.newValue;
            translateStore.eventOldValue = event.oldValue;
            if (translateStore.eventNewValue !== translateStore.eventOldValue && translateStore.eventNewValue) {
                translateStore.eventDataArr.set(event.data as TFlatJsonStruct);
                translateStore.sameValueArr.replace(foundSameValue(translateStore.gridDate, translateStore.eventDataArr.get().value));
                translateStore.changeSet.add(translateStore.eventDataArr.get().key);
                translateStore.changeSetBeforeAccept.add(translateStore.eventDataArr.get().key);
                if (translateStore.sameValueArr.length >= 1 && translateStore.eventNewValue !== '') {
                    translateStore.handleOpen();
                } else {
                    translateStore.changeSet.add(translateStore.eventDataArr.get().key);
                    translateStore.changeSetRest.delete(translateStore.eventDataArr.get().key);
                }
                translateStore.gridApi.redrawRows();
            }
        },
        rowClassRules: {
            'nextWordsKey': (node) => {
                return node.node.id === translateStore.nextWordsKey;
            },
            'bg-green': (node) => {
                return translateStore.changeSet.has(node.data.key) || (node.data as TFlatJsonStruct).translate === undefined;
            },
            'bg-yellow': (node) => {
                return translateStore.changeSetRest.has(node.data.key);
            },
            'bg-red': (node) => {
                return (node.data as TFlatJsonStruct).translate === '' || (node.data as TFlatJsonStruct).translate === undefined;
            }
        },
        stopEditingWhenCellsLoseFocus: true
    };

    useEffect(() => {
        const removeObserve = observe(translateStore.gridDate, (changeData) => {
            translateStore.gridApi.setGridOption('rowData', changeData.object);
        });

        let listener = () => translateStore.gridApi?.sizeColumnsToFit();
        window.addEventListener('resize', listener);

        return () => {
            removeObserve();
            window.removeEventListener('resize', listener);
            translateStore.gridApi = null;
        };
    });

    return (
        <div id={'table-translate'}>
            <Modal />
            <Alert />
            <TextField
                type='text'
                size='small'
                className={'nextWordsKey'}
                variant='outlined'
                label='Search...'
                sx={{ mb: 2 }}
                onChange={(event) => {
                    translateStore.gridApi.setGridOption('quickFilterText', event.target.value);
                }}
            />
            <Box display={'flex'} gap={2} height={'72vh'}>
                <TranslateCategory />
                <div style={{ width: '100%' }}>
                    <AgGridReact className='table ag-theme-balham' gridOptions={gridOptions as any} rowData={translateStore.gridDate} />
                </div>
            </Box>
        </div>
    );
};
